import { 
  AlertTriangle, UserX, CreditCard, BookX, Briefcase, HelpCircle,
  Users, FileCheck, GraduationCap, BadgeCheck, Building2, Database,
  ArrowRight, Flag
} from "lucide-react";

const JourneyVisual = () => {
  const leftSteps = [
    { label: "Abrupt exit from institutional care", icon: AlertTriangle },
    { label: "No family or financial safety net", icon: UserX },
    { label: "Missing identity & banking access", icon: CreditCard },
    { label: "Education discontinuity", icon: BookX },
    { label: "Survival-based informal work", icon: Briefcase },
    { label: "No structured aftercare navigation", icon: HelpCircle }
  ];

  const rightSteps = [
    { label: "Peer-led identification & trust building", icon: Users },
    { label: "Identity restoration & entitlement linkage", icon: FileCheck },
    { label: "Education & skill continuity pathways", icon: GraduationCap },
    { label: "Employability & placement support", icon: BadgeCheck },
    { label: "State aftercare integration", icon: Building2 },
    { label: "DHRUVA-enabled longitudinal tracking", icon: Database }
  ];

  return (
    <div className="journey-card w-full bg-white rounded-2xl shadow-lg border border-[#E5E7EB]" data-testid="journey-visual">
      {/* Header Section */}
      <div className="px-6 md:px-8 pt-6 md:pt-8 pb-4">
        {/* Section Label */}
        <div className="text-center mb-3">
          <p className="text-[10px] font-semibold text-[#6E6E6E] uppercase tracking-widest">
            The Post-18 Transition Gap
          </p>
        </div>

        {/* Headline */}
        <h3 className="text-xl md:text-2xl font-bold text-[#1E1E1E] text-center mb-2">
          At 18, Institutional Care Ends — <span className="text-[#8B6914]">System Risk Begins.</span>
        </h3>

        {/* Subline - Simplified */}
        <p className="text-sm text-[#6E6E6E] text-center mb-6 max-w-md mx-auto">
          Without structured aftercare continuity, vulnerability accelerates.
        </p>

        {/* Path Illustration - Simple Line Graphic */}
        <div className="flex justify-center mb-6">
          <svg width="400" height="40" viewBox="0 0 400 40" className="max-w-full">
            {/* Left: Broken dotted path */}
            <line x1="20" y1="20" x2="50" y2="20" stroke="#C9A227" strokeWidth="2" strokeDasharray="4 4" />
            <line x1="60" y1="20" x2="90" y2="20" stroke="#C9A227" strokeWidth="2" strokeDasharray="4 4" />
            <line x1="100" y1="20" x2="130" y2="20" stroke="#C9A227" strokeWidth="2" strokeDasharray="4 4" />
            <line x1="140" y1="20" x2="170" y2="20" stroke="#C9A227" strokeWidth="2" strokeDasharray="4 4" />
            
            {/* Center arrow */}
            <circle cx="200" cy="20" r="12" fill="#F5F5F5" stroke="#D1D5DB" strokeWidth="1" />
            <path d="M195 20 L205 20 M202 16 L206 20 L202 24" stroke="#1F7A5A" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
            
            {/* Right: Continuous path with flag */}
            <line x1="230" y1="20" x2="360" y2="20" stroke="#1F7A5A" strokeWidth="2" />
            <circle cx="370" cy="20" r="8" fill="#E8F2ED" stroke="#1F7A5A" strokeWidth="1.5" />
            <path d="M370 16 L370 24 M368 16 L374 18 L368 20" fill="#1F7A5A" />
          </svg>
        </div>
      </div>

      {/* Two Column Layout - Single White Background */}
      <div className="grid grid-cols-1 md:grid-cols-2 relative px-6 md:px-8 pb-6">
        
        {/* Center Divider with Arrow - Desktop Only */}
        <div className="hidden md:flex absolute left-1/2 top-0 bottom-0 flex-col items-center -translate-x-1/2">
          {/* Dotted vertical line */}
          <div className="w-px h-full border-l border-dashed border-[#D1D5DB]"></div>
          {/* Small arrow at top */}
          <div className="absolute top-4 flex flex-col items-center">
            <span className="text-[8px] font-medium text-[#9CA3AF] uppercase tracking-wider mb-1">
              Structured
            </span>
            <span className="text-[8px] font-medium text-[#9CA3AF] uppercase tracking-wider mb-1">
              Response
            </span>
            <ArrowRight className="w-3 h-3 text-[#1F7A5A]" />
          </div>
        </div>

        {/* LEFT COLUMN */}
        <div className="relative pr-0 md:pr-8" data-testid="system-gap-column">
          {/* Pill Label */}
          <div className="flex justify-center mb-3">
            <span className="text-[10px] font-semibold text-[#8B6914] bg-[#FDF8E8] border border-[#E5DDB8] px-3 py-1 rounded-full">
              Aftercare Break at 18
            </span>
          </div>

          {/* Column Title */}
          <p className="text-xs font-semibold text-[#5C4A1F] uppercase tracking-wider text-center mb-4">
            Post-18 Structural Reality
          </p>

          {/* Steps with Vertical Line */}
          <div className="relative pl-8">
            {/* Amber Vertical Line */}
            <div className="absolute left-3 top-2 bottom-2 w-0.5 bg-[#E5DFD3]"></div>
            <div className="left-progress-line absolute left-3 top-2 w-0.5 bg-[#C9A227]"></div>

            {/* Steps */}
            <div className="space-y-3">
              {leftSteps.map((step, index) => (
                <div 
                  key={index}
                  className="relative flex items-start gap-3"
                  data-testid={`left-step-${index}`}
                >
                  {/* Marker - Hollow */}
                  <div className={`left-dot-${index} absolute -left-5 top-1.5 w-2 h-2 rounded-full border-2 border-[#C9A227] bg-white`}></div>
                  
                  {/* Icon - Outline */}
                  <div className={`left-icon-${index} w-7 h-7 rounded-md flex items-center justify-center border border-[#D9C89E]`}>
                    <step.icon className="w-3.5 h-3.5 text-[#8B6914]" strokeWidth={1.5} />
                  </div>
                  
                  {/* Label */}
                  <span className={`left-label-${index} text-[13px] text-[#5C4A1F] pt-0.5`}>
                    {step.label}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Footer Line */}
          <p className="text-[10px] text-[#8B7355] text-center mt-4 italic">
            Instability compounds without coordinated intervention.
          </p>
        </div>

        {/* RIGHT COLUMN */}
        <div className="relative pl-0 md:pl-8 mt-8 md:mt-0" data-testid="clic-path-column">
          {/* Pill Label */}
          <div className="flex justify-center mb-3">
            <span className="text-[10px] font-semibold text-[#1F7A5A] bg-[#E8F2ED] border border-[#B8DCC8] px-3 py-1 rounded-full">
              CLiC's Structured Pathway
            </span>
          </div>

          {/* Column Title */}
          <p className="text-xs font-semibold text-[#1F7A5A] uppercase tracking-wider text-center mb-4">
            Structured Transition Through CLiC
          </p>

          {/* Steps with Vertical Line */}
          <div className="relative pl-8">
            {/* Green Vertical Line */}
            <div className="absolute left-3 top-2 bottom-2 w-0.5 bg-[#C8E0D4]"></div>
            <div className="right-progress-line absolute left-3 top-2 w-0.5 bg-[#1F7A5A]"></div>

            {/* Steps */}
            <div className="space-y-3">
              {rightSteps.map((step, index) => (
                <div 
                  key={index}
                  className="relative flex items-start gap-3"
                  data-testid={`right-step-${index}`}
                >
                  {/* Marker - Filled */}
                  <div className={`right-dot-${index} absolute -left-5 top-1.5 w-2 h-2 rounded-full bg-[#1F7A5A]`}></div>
                  
                  {/* Icon - Filled Background */}
                  <div className={`right-icon-${index} w-7 h-7 rounded-full flex items-center justify-center bg-[#1F7A5A]/10`}>
                    <step.icon className="w-3.5 h-3.5 text-[#1F7A5A]" strokeWidth={1.5} />
                  </div>
                  
                  {/* Label */}
                  <span className={`right-label-${index} text-[13px] text-[#2D5A47] font-medium pt-0.5`}>
                    {step.label}
                  </span>

                  {/* Final marker glow */}
                  {index === rightSteps.length - 1 && (
                    <div className="right-final-marker absolute -left-5 top-1.5 w-2 h-2 rounded-full bg-[#1F7A5A] ring-3 ring-[#1F7A5A]/20"></div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Footer Line */}
          <p className="text-[10px] text-[#1F7A5A] text-center mt-4 font-semibold">
            From abrupt exit to accountable independence.
          </p>
        </div>
      </div>

      {/* CSR Trigger Line */}
      <div className="px-6 md:px-8 py-5 border-t border-[#E5E7EB]">
        <p className="text-sm text-[#4A4A4A] text-center leading-relaxed max-w-xl mx-auto">
          CLiC's peer-led, system-integrated model{" "}
          <span className="font-semibold text-[#1E1E1E]">converts transition risk</span>{" "}
          into measurable livelihood outcomes — made possible through{" "}
          <span className="font-semibold text-[#1F7A5A]">committed CSR partnership.</span>
        </p>
      </div>

      {/* CSS Animation Styles */}
      <style>{`
        /* Left Column Animations */
        .left-progress-line {
          height: 0%;
          animation: leftProgress 12s ease-in-out infinite;
        }
        
        @keyframes leftProgress {
          0%, 3% { height: 0%; }
          45% { height: 100%; }
          50%, 100% { height: 100%; }
        }

        .left-dot-0, .left-dot-1, .left-dot-2, .left-dot-3, .left-dot-4, .left-dot-5 {
          transform: scale(0.5);
          opacity: 0.3;
        }
        
        .left-dot-0 { animation: leftDot0 12s ease-out infinite; }
        .left-dot-1 { animation: leftDot1 12s ease-out infinite; }
        .left-dot-2 { animation: leftDot2 12s ease-out infinite; }
        .left-dot-3 { animation: leftDot3 12s ease-out infinite; }
        .left-dot-4 { animation: leftDot4 12s ease-out infinite; }
        .left-dot-5 { animation: leftDot5 12s ease-out infinite; }

        @keyframes leftDot0 { 0%, 3% { transform: scale(0.5); opacity: 0.3; } 6%, 95% { transform: scale(1); opacity: 1; } 100% { transform: scale(0.5); opacity: 0.3; } }
        @keyframes leftDot1 { 0%, 9% { transform: scale(0.5); opacity: 0.3; } 12%, 95% { transform: scale(1); opacity: 1; } 100% { transform: scale(0.5); opacity: 0.3; } }
        @keyframes leftDot2 { 0%, 15% { transform: scale(0.5); opacity: 0.3; } 18%, 95% { transform: scale(1); opacity: 1; } 100% { transform: scale(0.5); opacity: 0.3; } }
        @keyframes leftDot3 { 0%, 21% { transform: scale(0.5); opacity: 0.3; } 24%, 95% { transform: scale(1); opacity: 1; } 100% { transform: scale(0.5); opacity: 0.3; } }
        @keyframes leftDot4 { 0%, 27% { transform: scale(0.5); opacity: 0.3; } 30%, 95% { transform: scale(1); opacity: 1; } 100% { transform: scale(0.5); opacity: 0.3; } }
        @keyframes leftDot5 { 0%, 33% { transform: scale(0.5); opacity: 0.3; } 36%, 95% { transform: scale(1); opacity: 1; } 100% { transform: scale(0.5); opacity: 0.3; } }

        .left-icon-0, .left-icon-1, .left-icon-2, .left-icon-3, .left-icon-4, .left-icon-5 {
          opacity: 0.3;
          transform: scale(0.9);
        }
        
        .left-icon-0 { animation: leftIcon0 12s ease-out infinite; }
        .left-icon-1 { animation: leftIcon1 12s ease-out infinite; }
        .left-icon-2 { animation: leftIcon2 12s ease-out infinite; }
        .left-icon-3 { animation: leftIcon3 12s ease-out infinite; }
        .left-icon-4 { animation: leftIcon4 12s ease-out infinite; }
        .left-icon-5 { animation: leftIcon5 12s ease-out infinite; }

        @keyframes leftIcon0 { 0%, 3% { opacity: 0.3; transform: scale(0.9); } 6%, 95% { opacity: 1; transform: scale(1); } 100% { opacity: 0.3; transform: scale(0.9); } }
        @keyframes leftIcon1 { 0%, 9% { opacity: 0.3; transform: scale(0.9); } 12%, 95% { opacity: 1; transform: scale(1); } 100% { opacity: 0.3; transform: scale(0.9); } }
        @keyframes leftIcon2 { 0%, 15% { opacity: 0.3; transform: scale(0.9); } 18%, 95% { opacity: 1; transform: scale(1); } 100% { opacity: 0.3; transform: scale(0.9); } }
        @keyframes leftIcon3 { 0%, 21% { opacity: 0.3; transform: scale(0.9); } 24%, 95% { opacity: 1; transform: scale(1); } 100% { opacity: 0.3; transform: scale(0.9); } }
        @keyframes leftIcon4 { 0%, 27% { opacity: 0.3; transform: scale(0.9); } 30%, 95% { opacity: 1; transform: scale(1); } 100% { opacity: 0.3; transform: scale(0.9); } }
        @keyframes leftIcon5 { 0%, 33% { opacity: 0.3; transform: scale(0.9); } 36%, 95% { opacity: 1; transform: scale(1); } 100% { opacity: 0.3; transform: scale(0.9); } }

        .left-label-0, .left-label-1, .left-label-2, .left-label-3, .left-label-4, .left-label-5 {
          opacity: 0;
          transform: translateX(-5px);
        }
        
        .left-label-0 { animation: leftLabel0 12s ease-out infinite; }
        .left-label-1 { animation: leftLabel1 12s ease-out infinite; }
        .left-label-2 { animation: leftLabel2 12s ease-out infinite; }
        .left-label-3 { animation: leftLabel3 12s ease-out infinite; }
        .left-label-4 { animation: leftLabel4 12s ease-out infinite; }
        .left-label-5 { animation: leftLabel5 12s ease-out infinite; }

        @keyframes leftLabel0 { 0%, 3% { opacity: 0; transform: translateX(-5px); } 6%, 95% { opacity: 1; transform: translateX(0); } 100% { opacity: 0; transform: translateX(-5px); } }
        @keyframes leftLabel1 { 0%, 9% { opacity: 0; transform: translateX(-5px); } 12%, 95% { opacity: 1; transform: translateX(0); } 100% { opacity: 0; transform: translateX(-5px); } }
        @keyframes leftLabel2 { 0%, 15% { opacity: 0; transform: translateX(-5px); } 18%, 95% { opacity: 1; transform: translateX(0); } 100% { opacity: 0; transform: translateX(-5px); } }
        @keyframes leftLabel3 { 0%, 21% { opacity: 0; transform: translateX(-5px); } 24%, 95% { opacity: 1; transform: translateX(0); } 100% { opacity: 0; transform: translateX(-5px); } }
        @keyframes leftLabel4 { 0%, 27% { opacity: 0; transform: translateX(-5px); } 30%, 95% { opacity: 1; transform: translateX(0); } 100% { opacity: 0; transform: translateX(-5px); } }
        @keyframes leftLabel5 { 0%, 33% { opacity: 0; transform: translateX(-5px); } 36%, 95% { opacity: 1; transform: translateX(0); } 100% { opacity: 0; transform: translateX(-5px); } }

        /* Right Column Animations */
        .right-progress-line {
          height: 0%;
          animation: rightProgress 12s ease-in-out infinite;
        }
        
        @keyframes rightProgress {
          0%, 3% { height: 0%; }
          45% { height: 100%; }
          50%, 100% { height: 100%; }
        }

        .right-dot-0, .right-dot-1, .right-dot-2, .right-dot-3, .right-dot-4, .right-dot-5 {
          transform: scale(0.5);
          opacity: 0.3;
        }
        
        .right-dot-0 { animation: rightDot0 12s ease-out infinite; }
        .right-dot-1 { animation: rightDot1 12s ease-out infinite; }
        .right-dot-2 { animation: rightDot2 12s ease-out infinite; }
        .right-dot-3 { animation: rightDot3 12s ease-out infinite; }
        .right-dot-4 { animation: rightDot4 12s ease-out infinite; }
        .right-dot-5 { animation: rightDot5 12s ease-out infinite; }

        @keyframes rightDot0 { 0%, 3% { transform: scale(0.5); opacity: 0.3; } 6%, 95% { transform: scale(1); opacity: 1; } 100% { transform: scale(0.5); opacity: 0.3; } }
        @keyframes rightDot1 { 0%, 9% { transform: scale(0.5); opacity: 0.3; } 12%, 95% { transform: scale(1); opacity: 1; } 100% { transform: scale(0.5); opacity: 0.3; } }
        @keyframes rightDot2 { 0%, 15% { transform: scale(0.5); opacity: 0.3; } 18%, 95% { transform: scale(1); opacity: 1; } 100% { transform: scale(0.5); opacity: 0.3; } }
        @keyframes rightDot3 { 0%, 21% { transform: scale(0.5); opacity: 0.3; } 24%, 95% { transform: scale(1); opacity: 1; } 100% { transform: scale(0.5); opacity: 0.3; } }
        @keyframes rightDot4 { 0%, 27% { transform: scale(0.5); opacity: 0.3; } 30%, 95% { transform: scale(1); opacity: 1; } 100% { transform: scale(0.5); opacity: 0.3; } }
        @keyframes rightDot5 { 0%, 33% { transform: scale(0.5); opacity: 0.3; } 36%, 95% { transform: scale(1); opacity: 1; } 100% { transform: scale(0.5); opacity: 0.3; } }

        .right-icon-0, .right-icon-1, .right-icon-2, .right-icon-3, .right-icon-4, .right-icon-5 {
          opacity: 0.3;
          transform: scale(0.9);
        }
        
        .right-icon-0 { animation: rightIcon0 12s ease-out infinite; }
        .right-icon-1 { animation: rightIcon1 12s ease-out infinite; }
        .right-icon-2 { animation: rightIcon2 12s ease-out infinite; }
        .right-icon-3 { animation: rightIcon3 12s ease-out infinite; }
        .right-icon-4 { animation: rightIcon4 12s ease-out infinite; }
        .right-icon-5 { animation: rightIcon5 12s ease-out infinite; }

        @keyframes rightIcon0 { 0%, 3% { opacity: 0.3; transform: scale(0.9); } 6%, 95% { opacity: 1; transform: scale(1); } 100% { opacity: 0.3; transform: scale(0.9); } }
        @keyframes rightIcon1 { 0%, 9% { opacity: 0.3; transform: scale(0.9); } 12%, 95% { opacity: 1; transform: scale(1); } 100% { opacity: 0.3; transform: scale(0.9); } }
        @keyframes rightIcon2 { 0%, 15% { opacity: 0.3; transform: scale(0.9); } 18%, 95% { opacity: 1; transform: scale(1); } 100% { opacity: 0.3; transform: scale(0.9); } }
        @keyframes rightIcon3 { 0%, 21% { opacity: 0.3; transform: scale(0.9); } 24%, 95% { opacity: 1; transform: scale(1); } 100% { opacity: 0.3; transform: scale(0.9); } }
        @keyframes rightIcon4 { 0%, 27% { opacity: 0.3; transform: scale(0.9); } 30%, 95% { opacity: 1; transform: scale(1); } 100% { opacity: 0.3; transform: scale(0.9); } }
        @keyframes rightIcon5 { 0%, 33% { opacity: 0.3; transform: scale(0.9); } 36%, 95% { opacity: 1; transform: scale(1); } 100% { opacity: 0.3; transform: scale(0.9); } }

        .right-label-0, .right-label-1, .right-label-2, .right-label-3, .right-label-4, .right-label-5 {
          opacity: 0;
          transform: translateX(-5px);
        }
        
        .right-label-0 { animation: rightLabel0 12s ease-out infinite; }
        .right-label-1 { animation: rightLabel1 12s ease-out infinite; }
        .right-label-2 { animation: rightLabel2 12s ease-out infinite; }
        .right-label-3 { animation: rightLabel3 12s ease-out infinite; }
        .right-label-4 { animation: rightLabel4 12s ease-out infinite; }
        .right-label-5 { animation: rightLabel5 12s ease-out infinite; }

        @keyframes rightLabel0 { 0%, 3% { opacity: 0; transform: translateX(-5px); } 6%, 95% { opacity: 1; transform: translateX(0); } 100% { opacity: 0; transform: translateX(-5px); } }
        @keyframes rightLabel1 { 0%, 9% { opacity: 0; transform: translateX(-5px); } 12%, 95% { opacity: 1; transform: translateX(0); } 100% { opacity: 0; transform: translateX(-5px); } }
        @keyframes rightLabel2 { 0%, 15% { opacity: 0; transform: translateX(-5px); } 18%, 95% { opacity: 1; transform: translateX(0); } 100% { opacity: 0; transform: translateX(-5px); } }
        @keyframes rightLabel3 { 0%, 21% { opacity: 0; transform: translateX(-5px); } 24%, 95% { opacity: 1; transform: translateX(0); } 100% { opacity: 0; transform: translateX(-5px); } }
        @keyframes rightLabel4 { 0%, 27% { opacity: 0; transform: translateX(-5px); } 30%, 95% { opacity: 1; transform: translateX(0); } 100% { opacity: 0; transform: translateX(-5px); } }
        @keyframes rightLabel5 { 0%, 33% { opacity: 0; transform: translateX(-5px); } 36%, 95% { opacity: 1; transform: translateX(0); } 100% { opacity: 0; transform: translateX(-5px); } }

        .right-final-marker {
          animation: finalMarkerPulse 12s ease-out infinite;
        }
        
        @keyframes finalMarkerPulse {
          0%, 33% { opacity: 0; }
          36%, 90% { opacity: 1; }
          95%, 100% { opacity: 0; }
        }
      `}</style>
    </div>
  );
};

export default JourneyVisual;
